import { useState, useRef, useEffect, useCallback } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import PropTypes from 'prop-types'

import rollbar, { sendWarning } from '@/utils/rollbar'
import config, { IS_PROD } from '@/config'
import usePrevious from '@/hooks/usePrevious'
import neat from '@/utils/neat'

import styles from './LoginPage.module.scss'

const TEST_RECAPTCHA_SCRIPT_ERROR = !IS_PROD && neat.flag('testRecaptchaScriptError')

const Recaptcha = ({ setRecaptchaToken, isRecaptchaVerifying, setIsRecaptchaVerifying, setRecaptchaError, badge = 'bottomleft', context = 'Recaptcha', siteKey }) => {
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false)
  const recaptchaRef = useRef()
  const recaptchaResetTimeout = useRef()
  const wasRecaptchaVerifying = usePrevious(isRecaptchaVerifying)
  const iframeObserver = useRef()
  const verifyingTimer = useRef()
  const logPrefix = `[${context}]`

  const handleRecaptchaReset = () => {
    // https://github.com/dozoisch/react-google-recaptcha/issues/129
    recaptchaResetTimeout.current = setTimeout(() => {
      try {
        if (isRecaptchaVerifying) {
          setIsRecaptchaVerifying(false)
        }
        recaptchaRef.current?.reset?.()
      } catch (e) {
        setRecaptchaError('Error resetting recaptcha')
        sendWarning(e, `${logPrefix} Error resetting recaptcha`)
      }
    }, 500)
  }

  const handleRecaptchaChange = async (token) => {
    if (!token) {
      return // challenge expired
    }
    setRecaptchaToken(token)
  }

  const handleCaptchaError = (err) => {
    rollbar.error(`${logPrefix} Error initializing recaptcha instance`, err)
    setRecaptchaError('Error initializing recaptcha')
    if (isRecaptchaVerifying) {
      setIsRecaptchaVerifying(false)
    }
  }

  // https://stackoverflow.com/questions/43488605/detect-when-challenge-window-is-closed-for-google-recaptcha/44984658#44984658
  const focusListener = useCallback((event) => {
    if (event.target.nodeName === 'IFRAME' && event.target.src.includes('/recaptcha/api2/bframe')) {
      const recaptchaWindow = event.target.parentNode?.parentNode
      if (recaptchaWindow) {
        iframeObserver.current = new MutationObserver(() => recaptchaWindow?.style.opacity === '0' && setIsRecaptchaVerifying(false))
          .observe(recaptchaWindow, { attributes: true, attributeFilter: ['style'] })
      }
    }
  }, [])

  useEffect(() => {
    let didAddFocusListener = false
    if (isRecaptchaVerifying && !wasRecaptchaVerifying) {
      if (!recaptchaLoaded) {
        setRecaptchaError('Recaptcha not loaded')
        sendWarning(null, `${logPrefix} Error loading recaptcha`)
        setIsRecaptchaVerifying(false)
        return undefined
      }

      recaptchaRef.current?.reset()
      recaptchaRef.current?.execute()
      document.addEventListener('focusin', focusListener)
      didAddFocusListener = true
      verifyingTimer.current = setTimeout(() => {
        // just in case google makes changes that break the workaround for detecting
        // when the recaptcha challenge is closed
        setIsRecaptchaVerifying(false)
      }, 5000)
    }
    return didAddFocusListener ? () => {
      document.removeEventListener('focusin', focusListener)
      clearTimeout(verifyingTimer.current)
      iframeObserver.current?.disconnect()
    } : undefined
  }, [isRecaptchaVerifying, recaptchaLoaded])

  useEffect(() => () => clearTimeout(recaptchaResetTimeout.current), [])

  return (
    <div className={badge === 'inline' ? styles.recaptchaContainer : undefined}>
      <ReCAPTCHA
        ref={recaptchaRef}
        asyncScriptOnLoad={TEST_RECAPTCHA_SCRIPT_ERROR ? undefined : () => setRecaptchaLoaded(true)}
        badge={badge}
        onChange={handleRecaptchaChange}
        onErrored={handleCaptchaError}
        onExpired={handleRecaptchaReset}
        sitekey={siteKey && !IS_PROD ? siteKey : config.recaptcha.siteKey}
        size="invisible"
      />
    </div>
  )
}

Recaptcha.propTypes = {
  isRecaptchaVerifying: PropTypes.bool,
  setRecaptchaToken: PropTypes.func.isRequired,
  setIsRecaptchaVerifying: PropTypes.func.isRequired,
  setRecaptchaError: PropTypes.func.isRequired,
  badge: PropTypes.string,
  context: PropTypes.string,
  siteKey: PropTypes.string,
}

export default Recaptcha
